import React from 'react';
import { styled } from '@linaria/react';
import PropTypes from 'prop-types';

import { useSettingsStore } from '@blocks/stores/settings';
import withErrorBoundary from '@blocks/utils/withErrorBoundary';

import { Skeleton } from '../ui/skeleton';
import { createMarkup } from '../utils/createMarkup';

import '../styles/theme.css';

const CompanyText = styled.div`
  font-family: inherit;
  white-space: pre-wrap;
  color: var(--bb-color-basic-3);
  font-size: var(--bb-fz-caption);
  line-height: var(--bb-lh-caption);
  text-align: left;

  &.companyText_center {
    white-space: normal;
    max-width: 450px;
    margin: 0 auto;
    text-align: center;
  }
`;

const CompanyHiddenText = styled(CompanyText)`
  &::after {
    content: attr(data-content);
  }
`;

const CompanySkeletonContainer = styled(CompanyText)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;

  &.companyText_center {
    flex-direction: row;
    margin: 0 auto;
    max-width: 600px;
    flex-wrap: wrap;
    justify-content: center;
  }
  & .company-skeleton {
    height: var(--bb-fz-caption);
    background-color: var(--bb-color-basic-3);

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

const CompanyDetails = ({ domain = '/', isPlainText = false, isCenter = false }) => {
  const { isLoading, settings } = useSettingsStore(domain);

  const preformattedCompanyData = `${settings?.company?.name},
Company Registration Number: ${settings?.company?.registration_number},
registered office is at ${settings?.company?.address_part_1}
${settings?.company?.address_part_2}
`;

  if (isLoading) {
    return (
      <CompanySkeletonContainer className={isCenter && 'companyText_center'}>
        <Skeleton
          className="company-skeleton"
          height="14px"
          style={{ width: '150px' }}
        />
        <Skeleton
          className="company-skeleton"
          style={{ width: '260px' }}
        />
        <Skeleton
          className="company-skeleton"
          style={{ width: '300px' }}
        />
        <Skeleton
          className="company-skeleton"
          style={{ width: '200px' }}
        />
      </CompanySkeletonContainer>
    );
  }

  if (!settings?.company && !isLoading) {
    return null;
  }

  if (isPlainText) {
    return (
      <CompanyText
        className={isCenter && 'companyText_center'}
        dangerouslySetInnerHTML={createMarkup(preformattedCompanyData)}
      />
    );
  }

  return (
    <CompanyHiddenText
      data-testid="company-details"
      className={isCenter && 'companyText_center'}
      data-content={preformattedCompanyData}
    />
  );
};

CompanyDetails.propTypes = {
  domain: PropTypes.string,
  isPlainText: PropTypes.bool,
  isCenter: PropTypes.bool,
};

export default withErrorBoundary(CompanyDetails);
